<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('baseObject.name')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.name"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="nameErrorMessage" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.code"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="codeErrorMessage" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('document-type.description')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.description"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('document-type.division')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownDivisions"
            :selected-item="selectedDivision"
            @update:selected="handleDivisionSelection"
            name="division"
            :mode="mode"
            show-error-as-tooltip
            clearable
            :error="divisionErrorMessage" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('document-type.company')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownCompanies"
            :selected-item="selectedCompany"
            @update:selected="handleCompanySelection"
            name="company"
            :mode="mode"
            show-error-as-tooltip
            :error="companyErrorMessage" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('document-type.parentDocumentType')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownParentDocumentTypes"
            :selected-item="selectedParentDocumentType"
            @update:selected="handleParentDocumentTypeSelection"
            name="parentDocumentType"
            :mode="mode"
            clearable />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive" />
          <BaseToggle v-else name="isActiveDocumentType" v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            :item="data.isFromManager ? data.isFromManager : 0"
            :textTrue="$t('common.actions.yes')"
            :textFalse="$t('common.actions.no')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.lastUpdate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.updateUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ updateUser || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.createdDate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ createUser || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.boid')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.boid || '-' }}
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { isoDateToFullDate } from '@/utils/date';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import type { DocumentType } from '@/types/DocumentTypes';
import { onMounted, ref, watch } from 'vue';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import type { SelectDropdownItem } from '@/types/Base';
import { getDocumentTypes } from '@/services/api/documents';

defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  codeErrorMessage: string;
  nameErrorMessage: string;
  divisionErrorMessage: string;
  companyErrorMessage: string;
}>();

const { getDivisionsLookup, getUsersLookup } = useLookupsStore();

const { dropdownCompanies, dropdownDivisions, companies, users } = storeToRefs(useLookupsStore());

const data = defineModel<DocumentType>();

const selectedDivision = ref<SelectDropdownItem | null>(null);
const selectedCompany = ref<SelectDropdownItem | null>(null);
const selectedParentDocumentType = ref<SelectDropdownItem | null>(null);
const createUser = ref<string>();
const updateUser = ref<string>();
const dropdownParentDocumentTypes = ref<SelectDropdownItem[]>([]);

watch(
  () => data.value,
  () => {
    initDropdowns();
  }
);

onMounted(async () => {
  await getDivisionsLookup();
  await getUsersLookup();
  initDropdowns();
});

async function loadParentDocumentTypes() {
  if (!data.value?.id) return;
  const parentDocTypes = await getDocumentTypes({
    canBeParent: true,
    documentTypeId: data.value.id,
  });
  dropdownParentDocumentTypes.value = parentDocTypes.map((dt) => ({
    value: dt.id,
    name: `${dt.name} (${dt.code})`,
  }));
}

function initDropdowns() {
  if (data.value) {
    const divisionValue = dropdownDivisions.value.find(
      (item) => item.value == data.value?.divisionID
    );
    if (divisionValue) selectedDivision.value = divisionValue;

    const companyValue = dropdownCompanies.value.find(
      (item) => item.value == data.value?.companyID
    );
    if (companyValue) selectedCompany.value = companyValue;

    const parentDocTypeValue = dropdownParentDocumentTypes.value.find(
      (item) => item.value == data.value?.parentDocumentTypeId
    );
    if (parentDocTypeValue) selectedParentDocumentType.value = parentDocTypeValue;

    const createUserValue = users.value.find((user) => user.id == data.value?.createUserId);
    if (createUserValue) createUser.value = createUserValue.name;

    const updateUserValue = users.value.find((user) => user.id == data.value?.updateUserId);
    if (updateUserValue) updateUser.value = updateUserValue.name;

    if (data.value.parentDocumentType) {
      selectedParentDocumentType.value = {
        value: data.value.parentDocumentType.id,
        name: `${data.value.parentDocumentType.name} (${data.value.parentDocumentType.code})`,
      };
    }
    loadParentDocumentTypes();
  }
}

function handleDivisionSelection(item: SelectDropdownItem) {
  if (!data.value) return;
  selectedDivision.value = item ? item : null;
  data.value.divisionID = item ? item.value.toString() : '';
}

function handleCompanySelection(item: SelectDropdownItem) {
  selectedCompany.value = item;
  if (data.value) {
    data.value.companyID = item.value.toString();
    data.value.company = companies.value.find((company) => company.id == item.value);
  }
}

function handleParentDocumentTypeSelection(item: SelectDropdownItem | null) {
  selectedParentDocumentType.value = item;
  if (data.value) {
    data.value.parentDocumentTypeId = item ? item.value.toString() : undefined;
  }
}
</script>
